import { useAsync } from '@nxsys/hooks/useAsync';
import {
  confirmMultipleDraftPayrollApi,
  deleteDraftPayrollByIdApi,
  downloadAllDraftPayrollApi,
  downloadAllPayrollApi,
  getDraftPayrollApi,
  getDraftPayrollDetailApi,
  getPayrollApi,
  getPayrollByIdApi,
  getPayrollOverviewDetailApi,
  getPayrollOverviewListingApi,
  recalculateDraftPayrollApi,
  reversalMultipleDraftPayrollApi,
  safeRecMultiplePayrollApi,
  saveDraftPayrollApi,
  sendEmailPayslipMultipleApi,
  sendPayslipApi,
  verifyPayslipApi,
  getPayslipVerificationApi,
  setPayslipGroundTruthApi,
  viewDraftPayslipByIdApi,
  viewPayslipByIdApi,
} from '@nxsys/services/api/payroll';
import type {
  PayrollOverviewGroup,
  PayrollOverviewDetailData,
} from '@nxsys/services/api/payroll';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import {
  DownloadAllDraftPayrollParams,
  DownloadAllPayrollParams,
  GetDraftPayrollParams,
  GetDraftPayrollResponse,
  ReversalMultipleDraftPayrollParams,
  SendPayslipByIdParams,
} from '@nxsys/store/slices/payroll/type';
import {
  GetPayrollListParams,
  GetPayrollListResponse,
} from '@nxsys/store/slices/report/types';
import { GetPayrollByIdResponse } from '@nxsys/store/slices/revenueFilings/types';
import {
  DraftPayrollDetailResponse,
  RecalculateParams,
  RecalculateResponse,
  SaveDraftParams,
  SaveDraftResponse,
} from '@nxsys/types/api/draft-payroll-recalc';
import { showMessage } from '@nxsys/utils/message';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';

export const useGetDraftPayroll = () => {
  const { value, loading, error, run } = useAsync<
    GetDraftPayrollResponse,
    GetDraftPayrollParams
  >(getDraftPayrollApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useDeleteDraftPayrollById = () => {
  const { value, loading, error, run } = useAsync<{ success: boolean }, string>(
    deleteDraftPayrollByIdApi,
  );

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useViewDraftPayslipById = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean; data: string },
    string
  >(viewDraftPayslipByIdApi);

  const _run = (id: string) => run(id);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

// NX-4971 — advisory payslip verifier re-run: ONE row (single-element array) or
// MANY (the Payment Summary bulk action). Both hit the same endpoint.
type VerifyPayslipBody = {
  draftPayrollIds?: string[];
  payrollIds?: string[];
};
export const useVerifyPayslip = () => {
  const { value, loading, error, run } = useAsync<
    {
      success: boolean;
      queued?: number;
      skipped?: { id: string; reason: string }[];
    },
    VerifyPayslipBody
  >(verifyPayslipApi);

  const _run = (body: VerifyPayslipBody) => run(body);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

// NX-4971 — fetch verdict detail + displayable input for the modal, by verification id.
export const useGetPayslipVerification = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean; data: any },
    { id: number }
  >(getPayslipVerificationApi);

  const _run = (query: { id: number }) => run(query);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

// NX-4971 — record a reviewer's "ground truth" verdict (required) + message (optional).
export const useSetPayslipGroundTruth = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean },
    { id: number; verdict: string; message?: string }
  >(setPayslipGroundTruthApi);

  const _run = (body: { id: number; verdict: string; message?: string }) =>
    run(body);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useDownloadAllDraftPayroll = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean },
    DownloadAllDraftPayrollParams
  >(downloadAllDraftPayrollApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useConfirmMultipleDraftPayroll = () => {
  const { value, loading, error, run } = useAsync<
    { message: string; success: boolean },
    { ids: string[], queryParams: { isBureau: boolean, isIreland: boolean } }
  >(({ ids, queryParams }) => confirmMultipleDraftPayrollApi(ids, queryParams));
  const { isBureau, payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const _run = (ids: string[]) => run({ ids, queryParams: { isBureau, isIreland } });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useReversalMultipleDraftPayroll = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean },
    ReversalMultipleDraftPayrollParams
  >(reversalMultipleDraftPayrollApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetPayroll = () => {
  const { value, loading, error, run } = useAsync<
    GetPayrollListResponse,
    GetPayrollListParams
  >(getPayrollApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useDownloadAllPayroll = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean },
    DownloadAllPayrollParams
  >(downloadAllPayrollApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSendEmailPayslipMultiple = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean },
    string[]
  >(sendEmailPayslipMultipleApi);

  const _run = (ids: string[]) => run(ids);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useViewPayslipById = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean; data: string },
    string
  >(viewPayslipByIdApi);

  const _run = (id: string) => run(id);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useSendPayslip = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean; data: string },
    { id: string } & SendPayslipByIdParams
  >(({ id, ...params }) => sendPayslipApi(id, params));

  const _run = ({ id, ...params }: { id: string } & SendPayslipByIdParams) =>
    run({ id, ...params });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useSafeRecMultiplePayroll = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean, errorMessage?: string[] },
    string[]
  >(safeRecMultiplePayrollApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    } else if (value && !value?.success) {
      showMessage({
        type: 'error',
        content: [...new Set(value?.errorMessage)].join(', ') || 'Failed to send payslip to SafeRec',
      });
    }
  }, [error, value]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetPayrollById = () => {
  const { value, loading, error, run } = useAsync<
    GetPayrollByIdResponse,
    string
  >(getPayrollByIdApi);


  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetDraftPayrollDetail = () => {
  const { value, loading, error, run } = useAsync<
    DraftPayrollDetailResponse,
    string
  >(getDraftPayrollDetailApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useRecalculateDraftPayroll = () => {
  const { value, loading, error, run } = useAsync<
    RecalculateResponse,
    { draftPayrollId: string; params: RecalculateParams }
  >(({ draftPayrollId, params }) =>
    recalculateDraftPayrollApi(draftPayrollId, params),
  );

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSaveDraftPayroll = () => {
  const { value, loading, error, run } = useAsync<
    SaveDraftResponse,
    { draftPayrollId: string; params: SaveDraftParams }
  >(({ draftPayrollId, params }) =>
    saveDraftPayrollApi(draftPayrollId, params),
  );

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  useEffect(() => {
    if (value?.success) {
      showMessage({ type: 'success', content: 'Payslip saved successfully' });
    }
  }, [value]);

  return {
    value,
    loading,
    error,
    run,
  };
};

// --- Payroll Overview hooks ---

export const useGetPayrollOverviewListing = () => {
  const { value, loading, error, run } = useAsync<
    { data: PayrollOverviewGroup[]; success: boolean },
    { search?: string; payFrequency?: string; taxPeriod?: number; status?: string } | undefined
  >(getPayrollOverviewListingApi);

  useEffect(() => {
    if (error) {
      showMessage({ type: 'error', content: getErrorMessage(error) });
    }
  }, [error]);

  return { value, loading, error, run };
};

export const useGetPayrollOverviewDetail = () => {
  const { value, loading, error, run } = useAsync<
    { data: PayrollOverviewDetailData; success: boolean },
    { companyId: string; taxPeriod: number; payFrequency: string }
  >(({ companyId, ...params }) => getPayrollOverviewDetailApi(companyId, params));

  useEffect(() => {
    if (error) {
      showMessage({ type: 'error', content: getErrorMessage(error) });
    }
  }, [error]);

  return { value, loading, error, run };
};
